import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";

export type StatusKind =
  | "success"
  | "pending"
  | "suspended"
  | "error"
  | "inactive"
  | "default";

export interface StatusBadgeProps {
  /** A platform domain status, case-insensitive (for example, "IN_REVIEW"). */
  status: string;
}

const STATUS_KIND: Record<string, StatusKind> = {
  pending: "pending",
  in_review: "pending",
  under_review: "pending",
  in_transit: "pending",
  active: "success",
  certified: "success",
  approved: "success",
  executed: "success",
  resolved: "success",
  published: "success",
  paid: "success",
  matched: "success",
  suspended: "suspended",
  banned: "error",
  rejected: "error",
  cancelled: "error",
  canceled: "error",
  failed: "error",
  expired: "error",
  draft: "inactive",
  archived: "inactive",
  inactive: "inactive",
  short: "default",
  over: "default",
  not_applicable: "default",
};

const CHIP_TONE: Record<StatusKind, Pick<ChipProps, "color" | "variant">> = {
  success: { color: "success", variant: "outlined" },
  pending: { color: "warning", variant: "outlined" },
  suspended: { color: "warning", variant: "filled" },
  error: { color: "error", variant: "outlined" },
  inactive: { color: "default", variant: "outlined" },
  default: { color: "default", variant: "outlined" },
};

function kindFor(status: string): StatusKind {
  return STATUS_KIND[status.trim().toLowerCase()] ?? "default";
}

/** "in_review" becomes "In review" while preserving the source token's case. */
function humanize(status: string): string {
  const normalized = status.trim().replace(/_/g, " ");
  if (!normalized) return status;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Native MUI status chip. Text communicates the status independently of color;
 * the stable data attribute exposes the resolved domain intent to tests.
 */
export function StatusBadge({ status }: StatusBadgeProps) {
  const kind = kindFor(status);
  const tone = CHIP_TONE[kind];

  return (
    <Chip
      size="small"
      label={humanize(status)}
      color={tone.color}
      variant={tone.variant}
      data-status-kind={kind}
    />
  );
}
